import { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { S } from '../../utils/constants';
import { SalesKPIs } from './SalesKPIs';

/**
 * Sales Manager Dashboard
 * Shows team overview, live feed, expiring leads, and reassignment controls
 */
export function SalesManagerDashboard({ currentUser, restaurants, users }) {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'sales'
  const [viewRepModal, setViewRepModal] = useState(null); // View rep details
  const [stats, setStats] = useState({
    totalReps: 0,
    activeClaims: 0,
    expiringLeads: 0,
    totalOnboarded: 0,
  });
  const [repPerformance, setRepPerformance] = useState([]);
  const [liveFeed, setLiveFeed] = useState([]);
  const [expiringLeads, setExpiringLeads] = useState([]);
  const [reassignModal, setReassignModal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get all reps
      const { data: reps } = await supabase
        .from('users')
        .select('id, name, role')
        .eq('role', 'rep');

      // Get all claimed restaurants
      const { data: claimedRestaurants } = await supabase
        .from('restaurants')
        .select('*')
        .eq('status', 'claimed')
        .not('claimed_by', 'is', null);

      // Get expiring leads (claimed > 36 hours ago)
      const thirtySixHoursAgo = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString();
      const { data: expiring } = await supabase
        .from('restaurants')
        .select('*, claimed_by_user:users!claimed_by(name)')
        .eq('status', 'claimed')
        .lt('claimed_at', thirtySixHoursAgo);

      // Get recently onboarded (deal_closed in last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: onboarded } = await supabase
        .from('restaurants')
        .select('*')
        .eq('status', 'deal_closed')
        .gte('updated_at', sevenDaysAgo);

      // Get system logs for live feed
      const { data: logs } = await supabase
        .from('system_logs')
        .select('*, actor:users!actor_id(name, role)')
        .order('created_at', { ascending: false })
        .limit(20);

      // Calculate rep performance with ALL KPIs
      const repStats = (reps || []).map(rep => {
        const repClaims = (claimedRestaurants || []).filter(r => r.claimed_by === rep.id);
        const repOnboarded = (onboarded || []).filter(r => r.claimed_by === rep.id);
        
        // Calculate total claims ever (for conversion rate)
        const totalClaimsEver = repClaims.length + repOnboarded.length;
        const conversionRate = totalClaimsEver > 0 ? Math.round((repOnboarded.length / totalClaimsEver) * 100) : 0;
        
        // Calculate average time to close (for onboarded deals)
        let avgTimeToClose = 0;
        if (repOnboarded.length > 0) {
          const totalTime = repOnboarded.reduce((sum, r) => {
            const claimed = new Date(r.claimed_at).getTime();
            const closed = new Date(r.updated_at).getTime();
            return sum + (closed - claimed);
          }, 0);
          avgTimeToClose = Math.round(totalTime / repOnboarded.length / (1000 * 60 * 60)); // hours
        }
        
        // Calculate revenue (assuming commission from restaurants)
        const totalRevenue = repOnboarded.reduce((sum, r) => sum + (r.commission || 0), 0);
        
        // Last interaction
        const lastInteraction = repClaims.length > 0 
          ? Math.max(...repClaims.map(r => new Date(r.last_interaction_at || r.claimed_at).getTime()))
          : null;
        
        return {
          id: rep.id,
          name: rep.name,
          activeClaims: repClaims.length,
          onboarded: repOnboarded.length,
          totalClaims: totalClaimsEver,
          conversionRate,
          avgTimeToClose,
          totalRevenue,
          lastInteraction,
        };
      });

      setStats({
        totalReps: (reps || []).length,
        activeClaims: (claimedRestaurants || []).length,
        expiringLeads: (expiring || []).length,
        totalOnboarded: (onboarded || []).length,
      });

      setRepPerformance(repStats);
      setExpiringLeads(expiring || []);
      setLiveFeed(logs || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async (restaurantId, newRepId) => {
    try {
      // Get restaurant details
      const restaurant = restaurants.find(r => r.id === restaurantId);
      const newRep = users.find(u => u.id === newRepId);
      const oldRep = users.find(u => u.id === restaurant.claimed_by);

      // Update restaurant
      const { error } = await supabase
        .from('restaurants')
        .update({
          claimed_by: newRepId,
          claimed_at: new Date().toISOString(),
          last_interaction_at: null,
        })
        .eq('id', restaurantId);

      if (error) throw error;

      // Log the reassignment
      await supabase
        .from('system_logs')
        .insert({
          action: 'lead_reassigned',
          actor_id: currentUser.id,
          actor_name: currentUser.name,
          target_id: restaurantId,
          target_name: restaurant.name,
          old_value: {
            claimed_by: oldRep?.name,
            claimed_by_id: restaurant.claimed_by,
          },
          new_value: {
            claimed_by: newRep?.name,
            claimed_by_id: newRepId,
          },
        });

      showToast(`✅ تم نقل "${restaurant.name}" إلى ${newRep?.name}`);
      setReassignModal(null);
      loadDashboardData();
    } catch (err) {
      console.error('Reassign error:', err);
      showToast('❌ فشل في نقل المطعم', 'error');
    }
  };

  const showToast = (msg, type = 'success') => {
    // Use parent's showToast if available, otherwise alert
    if (window.showToast) {
      window.showToast(msg, type);
    } else {
      alert(msg);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>جاري التحميل...</div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          👋 مرحباً {currentUser.name}
        </h1>
        <p style={{ color: '#000000', fontSize: 16, marginBottom: 24 }}>
          نظرة عامة على أداء فريق المبيعات
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '2px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'overview' ? '3px solid #4ecdc4' : '3px solid transparent',
              color: activeTab === 'overview' ? '#4ecdc4' : '#64748b',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'Cairo', sans-serif",
              transition: 'all 0.2s',
            }}
          >
            📊 نظرة عامة
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'sales' ? '3px solid #10b981' : '3px solid transparent',
              color: activeTab === 'sales' ? '#10b981' : '#64748b',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'Cairo', sans-serif",
              transition: 'all 0.2s',
            }}
          >
            📈 مؤشرات المبيعات
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
      <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard
          icon="👥"
          label="المندوبين النشطين"
          value={stats.totalReps}
          color="#4ecdc4"
        />
        <StatCard
          icon="📋"
          label="المطاعم المعلّقة"
          value={stats.activeClaims}
          color="#f59e0b"
        />
        <StatCard
          icon="⏰"
          label="مطاعم على وشك الانتهاء"
          value={stats.expiringLeads}
          color="#ef4444"
        />
        <StatCard
          icon="✅"
          label="تم الانضمام هذا الأسبوع"
          value={stats.totalOnboarded}
          color="#10b981"
        />
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
        {/* Rep Performance */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24 }}>
          <h2 style={{ color: '#000000', fontSize: 20, fontWeight: 700, marginBottom: 20 }}>
            📊 أداء المندوبين
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {repPerformance.map(rep => {
              const repUser = users.find(u => u.id === rep.id);
              const phone = repUser?.phone;
              
              return (
              <div key={rep.id} style={{
                padding: 16,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => setViewRepModal(rep)}
              onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
              onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ color: '#000000', fontSize: 16, fontWeight: 700 }}>
                      {rep.name}
                    </span>
                    {!repUser?.is_approved_to_work && (
                      <span style={{
                        padding: '2px 8px',
                        background: '#f59e0b22',
                        border: '1px solid #f59e0b44',
                        borderRadius: 6,
                        color: '#f59e0b',
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        تحت التدريب
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>
                    {rep.activeClaims} مطعم معلّق • {rep.onboarded} تم انضمامهم
                  </div>
                  {phone && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          window.open(`tel:${phone}`, '_self');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#4ecdc422',
                          border: '1px solid #4ecdc444',
                          borderRadius: 6,
                          color: '#60a5fa',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: "'Cairo', sans-serif",
                        }}
                      >
                        📞 اتصال
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const cleanPhone = phone.replace(/\D/g, '');
                          window.open(`https://wa.me/971${cleanPhone}`, '_blank');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#10b98122',
                          border: '1px solid #10b98144',
                          borderRadius: 6,
                          color: '#10b981',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: "'Cairo', sans-serif",
                        }}
                      >
                        💬 واتساب
                      </button>
                    </div>
                  )}
                </div>
                <div style={{
                  padding: '8px 16px',
                  background: '#4ecdc422',
                  border: '1px solid #4ecdc444',
                  borderRadius: 8,
                  color: '#60a5fa',
                  fontSize: 14,
                  fontWeight: 700,
                }}>
                  {rep.activeClaims + rep.onboarded} إجمالي
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Live Feed */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24 }}>
          <h2 style={{ color: '#000000', fontSize: 20, fontWeight: 700, marginBottom: 20 }}>
            📡 النشاط المباشر
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflow: 'auto' }}>
            {liveFeed.map(log => (
              <div key={log.id} style={{
                padding: 12,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 13,
              }}>
                <div style={{ color: '#000000', fontWeight: 700, marginBottom: 4 }}>
                  {log.actor?.name || 'Unknown'}
                </div>
                <div style={{ color: '#000000' }}>
                  {log.action === 'lead_reassigned' ? (
                    <>نقل "{log.target_name}" من {log.old_value?.claimed_by} إلى {log.new_value?.claimed_by}</>
                  ) : (
                    <>{log.action}</>
                  )}
                </div>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
                  {new Date(log.created_at).toLocaleString('ar-SA')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expiring Leads */}
      {expiringLeads.length > 0 && (
        <div style={{ background: '#ffffff', border: '1px solid #ef444444', borderRadius: 16, padding: 24, marginBottom: 32 }}>
          <h2 style={{ color: '#ef4444', fontSize: 20, fontWeight: 700, marginBottom: 20 }}>
            ⚠️ مطاعم على وشك الانتهاء (48 ساعة)
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {expiringLeads.map(restaurant => {
              const hoursLeft = Math.max(0, Math.floor((48 * 60 * 60 * 1000 - (Date.now() - new Date(restaurant.claimed_at).getTime())) / (60 * 60 * 1000)));
              
              return (
                <div key={restaurant.id} style={{
                  padding: 16,
                  background: hoursLeft < 12 ? '#ef444411' : '#f59e0b11',
                  border: `1px solid ${hoursLeft < 12 ? '#ef444444' : '#f59e0b44'}`,
                  borderRadius: 12,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div>
                    <div style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                      {restaurant.name}
                    </div>
                    <div style={{ color: '#000000', fontSize: 13 }}>
                      المندوب: {restaurant.claimed_by_user?.name} • متبقي {hoursLeft} ساعة
                    </div>
                  </div>
                  <button
                    onClick={() => setReassignModal(restaurant)}
                    style={{
                      padding: '8px 16px',
                      background: '#4ecdc4',
                      border: 'none',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    🔄 نقل
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}

      {activeTab === 'sales' && (
        <SalesKPIs repPerformance={repPerformance} />
      )}

      {/* Reassignment Modal */}
      {reassignModal && (
        <ReassignModal
          restaurant={reassignModal}
          users={users}
          onClose={() => setReassignModal(null)}
          onReassign={handleReassign}
        />
      )}

      {/* View Rep Details Modal */}
      {viewRepModal && (
        <ViewRepModal
          rep={viewRepModal}
          users={users}
          restaurants={restaurants}
          onClose={() => setViewRepModal(null)}
        />
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 16,
      padding: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 32 }}>{icon}</span>
        <div>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>{label}</div>
          <div style={{ color, fontSize: 32, fontWeight: 900 }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// Reassign Modal Component
function ReassignModal({ restaurant, users, onClose, onReassign }) {
  const [selectedRep, setSelectedRep] = useState('');

  const reps = users.filter(u => u.role === 'rep');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 24,
    }}>
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: 32,
        maxWidth: 500,
        width: '100%',
      }}>
        <h2 style={{ color: '#000000', fontSize: 22, fontWeight: 900, marginBottom: 24 }}>
          🔄 نقل المطعم
        </h2>

        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#000000', fontSize: 14, marginBottom: 8 }}>المطعم:</div>
          <div style={{ color: '#000000', fontSize: 18, fontWeight: 700 }}>{restaurant.name}</div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', color: '#000000', marginBottom: 8, fontSize: 14 }}>
            نقل إلى مندوب:
          </label>
          <select
            value={selectedRep}
            onChange={e => setSelectedRep(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              background: '#ffffff',
              border: '2px solid #e2e8f0',
              borderRadius: 10,
              color: '#000000',
              fontSize: 16,
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            <option value="">اختر مندوب...</option>
            {reps.map(rep => (
              <option key={rep.id} value={rep.id}>{rep.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => onReassign(restaurant.id, selectedRep)}
            disabled={!selectedRep}
            style={{
              flex: 1,
              padding: '12px',
              background: selectedRep ? '#4ecdc4' : '#64748b',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: selectedRep ? 'pointer' : 'not-allowed',
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            ✅ تأكيد النقل
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: '#e2e8f0',
              border: 'none',
              borderRadius: 8,
              color: '#000000',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

// View Rep Details Modal (View Only)
function ViewRepModal({ rep, users, restaurants, onClose }) {
  const repUser = users.find(u => u.id === rep.id);
  const repRestaurants = restaurants.filter(r => r.claimed_by === rep.id);
  const activeClaims = repRestaurants.filter(r => r.status === 'claimed');
  const closedDeals = repRestaurants.filter(r => r.status === 'deal_closed');

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: 32,
        maxWidth: 700,
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ color: '#000000', fontSize: 22, fontWeight: 900, margin: 0 }}>
              👤 تفاصيل المندوب: {rep.name}
            </h2>
            {!repUser?.is_approved_to_work && (
              <span style={{
                padding: '4px 12px',
                background: '#f59e0b22',
                border: '1px solid #f59e0b44',
                borderRadius: 8,
                color: '#f59e0b',
                fontSize: 13,
                fontWeight: 700,
              }}>
                تحت التدريب
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: '#e2e8f0',
              border: 'none',
              borderRadius: 8,
              color: '#000000',
              fontSize: 16,
              cursor: 'pointer',
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            ✕
          </button>
        </div>

        {/* Profile Info */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📋 معلومات الحساب</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>الاسم:</span>
              <span style={{ color: '#000000', fontWeight: 700 }}>{repUser?.name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>الهاتف:</span>
              <span style={{ color: '#000000', fontWeight: 700 }}>{repUser?.phone || 'غير متوفر'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>البريد الإلكتروني:</span>
              <span style={{ color: '#000000', fontWeight: 700 }}>{repUser?.email || 'غير متوفر'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>الدور:</span>
              <span style={{ color: '#4ecdc4', fontWeight: 700 }}>مندوب مبيعات</span>
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>المطالبات النشطة</div>
            <div style={{ color: '#f59e0b', fontSize: 28, fontWeight: 900 }}>{rep.activeClaims}</div>
          </div>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>صفقات مغلقة</div>
            <div style={{ color: '#10b981', fontSize: 28, fontWeight: 900 }}>{rep.onboarded}</div>
          </div>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>معدل التحويل</div>
            <div style={{ color: '#4ecdc4', fontSize: 28, fontWeight: 900 }}>{rep.conversionRate}%</div>
          </div>
        </div>

        {/* Assigned Restaurants */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            🏠 المطاعم المعيّنة ({repRestaurants.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {repRestaurants.slice(0, 10).map(r => (
              <div key={r.id} style={{
                padding: 12,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>{r.name}</div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>
                    {new Date(r.claimed_at).toLocaleDateString('ar-SA')}
                  </div>
                </div>
                <span style={{
                  padding: '4px 10px',
                  background: r.status === 'claimed' ? '#f59e0b22' : '#10b98122',
                  color: r.status === 'claimed' ? '#f59e0b' : '#10b981',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  {r.status === 'claimed' ? 'معلّق' : 'مغلق'}
                </span>
              </div>
            ))}
            {repRestaurants.length > 10 && (
              <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 8 }}>
                ... و{repRestaurants.length - 10} مطاعم أخرى
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
